/**
 * Dashboard Page
 * User's plan management dashboard
 */

import { Link } from 'react-router-dom';
import { usePlans } from '../hooks/usePlans';
import { useAuth } from '../contexts/AuthContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { safeJsonParse, isPlainObject } from '@/lib/utils';

export function Dashboard() {
  const { plans, isLoading } = usePlans();
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <h1 className="text-3xl font-bold text-neutral-900 mb-4">My Plans</h1>
        <p className="text-neutral-600 mb-6">Please log in to view your plans.</p>
        <Link to="/plans/create" className="btn-primary">
          Get Started
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-neutral-600">Loading your plans...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary resetKeys={[user?.email || '']}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900 mb-2">My Plans</h1>
            <p className="text-neutral-600">
              Welcome back, {user?.email.split('@')[0]}
            </p>
          </div>
          <Link to="/plans/create" className="btn-primary">
            Create New Plan
          </Link>
        </div>

        {/* Plans Grid */}
        {plans.length === 0 ? (
          <div className="card text-center py-12">
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">No plans yet</h2>
            <p className="text-neutral-600 mb-6">
              Get started by creating your first waste management plan
            </p>
            <Link to="/plans/create" className="btn-primary">
              Create Your First Plan
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => {
              // Parse plan_data to get project info
              const parsed = safeJsonParse<Record<string, unknown>>(
                plan.plan_data,
                isPlainObject,
                {}
              );
              const planData = parsed as { project?: { address?: string } };

              return (
                <Link
                  key={plan.id}
                  to={`/plans/${plan.id}`}
                  className="card hover:shadow-lg transition-shadow"
                >
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                      {planData.project?.address || 'Untitled Plan'}
                    </h3>
                    <p className="text-sm text-neutral-600">
                      {plan.jurisdiction_id || 'No jurisdiction'}
                    </p>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      plan.status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : plan.status === 'submitted'
                        ? 'bg-blue-100 text-blue-800'
                        : plan.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-neutral-100 text-neutral-800'
                    }`}>
                      {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
                    </span>
                    <span className="text-neutral-500">
                      {new Date(plan.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}